import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const WeatherFilter = ({ 
  onFilterChange, 
  onClearFilters, 
  compact = false,
  title = "Filter by Weather Conditions"
}) => {
  const [filters, setFilters] = useState({
    // Location filter
    location: '',
    
    // Temperature ranges
    snow_temp_min: '',
    snow_temp_max: '',
    air_temp_min: '',
    air_temp_max: '',
    
    // Humidity ranges
    snow_humidity_min: '',
    snow_humidity_max: '',
    air_humidity_min: '',
    air_humidity_max: '',
    
    // Clouds range
    clouds_min: '',
    clouds_max: '',
    
    // Dropdown filters
    visibility: '',
    wind: '',
    precipitation: '',
    artificial_snow: '',
    natural_snow: '',
    grain_size: '',
    snow_humidity: '',
    track_hardness: '',
    track_shininess: '',
    track_dirtiness: ''
  });

  const [expanded, setExpanded] = useState(false);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = Object.keys(filters).reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {});
    setFilters(clearedFilters);
    onClearFilters();
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  const visibilityOptions = [
    { value: 'unlimited', label: 'Unlimited' },
    { value: '10km', label: '10 km' },
    { value: '5km', label: '5 km' },
    { value: '2km', label: '2 km' },
    { value: '1km', label: '1 km' },
    { value: '500m', label: '500 m' },
    { value: '200m', label: '200 m' },
    { value: '100m', label: '100 m' },
    { value: '50m', label: '50 m' },
    { value: '10m', label: '10 m' },
    { value: '5m', label: '5 m' },
    { value: '1m', label: '1 m' }
  ];

  const windOptions = [
    { value: 'calm', label: 'Calm' },
    { value: 'light', label: 'Light Breeze' },
    { value: 'moderate', label: 'Moderate Breeze' },
    { value: 'fresh', label: 'Fresh Breeze' },
    { value: 'strong', label: 'Strong Breeze' },
    { value: 'gale', label: 'Gale' },
    { value: 'storm', label: 'Storm' },
    { value: 'extreme', label: 'Extreme' }
  ];

  const precipitationOptions = [
    { value: 'none', label: 'None' },
    { value: 'snow', label: 'Snow' },
    { value: 'light_snow', label: 'Light Snow' },
    { value: 'heavy_snow', label: 'Heavy Snow' },
    { value: 'rain', label: 'Rain' },
    { value: 'light_rain', label: 'Light Rain' },
    { value: 'heavy_rain', label: 'Heavy Rain' },
    { value: 'sleet', label: 'Sleet' },
    { value: 'hail', label: 'Hail' }
  ];

  const artificialSnowOptions = [
    { value: 'A1', label: 'A1 (Falling new)' },
    { value: 'A2', label: 'A2 (New)' },
    { value: 'A3', label: 'A3 (Irregularly decomposed new snow)' },
    { value: 'A4', label: 'A4 (Irregularly decomposed transformed snow)' },
    { value: 'A5', label: 'A5 (Transformed)' }
  ];

  const naturalSnowOptions = [
    { value: 'FN', label: 'FN (Falling New)' },
    { value: 'NS', label: 'NS (New)' },
    { value: 'IN', label: 'IN (Irregularly decomposed new)' },
    { value: 'IT', label: 'IT (Irregularly decomposed transformed snow)' },
    { value: 'TR', label: 'TR (Transformed)' }
  ];

  const grainSizeOptions = [
    { value: 'G0', label: 'G0 (0.0-0.2mm - Extra fine)' },
    { value: 'G1', label: 'G1 (0.2-0.5mm - Very fine)' },
    { value: 'G2', label: 'G2 (0.5-1.0mm - Fine)' },
    { value: 'G3', label: 'G3 (2.0-4.0mm - Coarse)' },
    { value: 'G5', label: 'G5 (>4.0mm - Very coarse)' }
  ];

  const snowHumidityOptions = [
    { value: 'DS', label: 'DS (Dry)' },
    { value: 'W1', label: 'W1 (Moist)' },
    { value: 'W2', label: 'W2 (Wet)' },
    { value: 'W3', label: 'W3 (Very wet)' },
    { value: 'W4', label: 'W4 (Slush)' }
  ];

  const trackHardnessOptions = [
    { value: 'H1', label: 'H1 (Very soft)' },
    { value: 'H2', label: 'H2 (Soft)' },
    { value: 'H3', label: 'H3 (Medium hard)' },
    { value: 'H4', label: 'H4 (Hard)' },
    { value: 'H5', label: 'H5 (Very hard)' },
    { value: 'H6', label: 'H6 (Ice)' }
  ];

  const trackShininessOptions = [
    { value: 'T0', label: 'T0 (Matte)' },
    { value: 'T1', label: 'T1 (Partly shiny)' },
    { value: 'T2', label: 'T2 (Shiny)' }
  ];

  const trackDirtinessOptions = [
    { value: 'D0', label: 'D0 (Clean)' },
    { value: 'D1', label: 'D1 (Partly dirty)' },
    { value: 'D2', label: 'D2 (Dirty)' }
  ];

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tooltip title="Filter by weather conditions">
          <IconButton onClick={() => setExpanded(!expanded)} color="primary">
            <FilterListIcon />
          </IconButton>
        </Tooltip>
        {getActiveFilterCount() > 0 && (
          <Chip 
            label={`${getActiveFilterCount()} filters`} 
            size="small" 
            color="primary" 
            onDelete={handleClearFilters}
          />
        )}
        {expanded && (
          <Box sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, bgcolor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mt: 1 }}>
            <WeatherFilterContent 
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              visibilityOptions={visibilityOptions}
              windOptions={windOptions}
              precipitationOptions={precipitationOptions}
              artificialSnowOptions={artificialSnowOptions}
              naturalSnowOptions={naturalSnowOptions}
              grainSizeOptions={grainSizeOptions}
              snowHumidityOptions={snowHumidityOptions}
              trackHardnessOptions={trackHardnessOptions}
              trackShininessOptions={trackShininessOptions}
              trackDirtinessOptions={trackDirtinessOptions}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon color="primary" />
            <Typography variant="h6">
              {title}
            </Typography>
            {getActiveFilterCount() > 0 && (
              <Chip 
                label={`${getActiveFilterCount()} active`} 
                size="small" 
                color="primary" 
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <WeatherFilterContent 
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            visibilityOptions={visibilityOptions}
            windOptions={windOptions}
            precipitationOptions={precipitationOptions}
            artificialSnowOptions={artificialSnowOptions}
            naturalSnowOptions={naturalSnowOptions}
            grainSizeOptions={grainSizeOptions}
            snowHumidityOptions={snowHumidityOptions}
            trackHardnessOptions={trackHardnessOptions}
            trackShininessOptions={trackShininessOptions}
            trackDirtinessOptions={trackDirtinessOptions}
          />
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

const WeatherFilterContent = ({
  filters,
  onFilterChange,
  onClearFilters,
  visibilityOptions,
  windOptions,
  precipitationOptions,
  artificialSnowOptions,
  naturalSnowOptions,
  grainSizeOptions,
  snowHumidityOptions,
  trackHardnessOptions,
  trackShininessOptions,
  trackDirtinessOptions
}) => {
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Set ranges and select specific conditions to filter entries
        </Typography>
        <Button 
          startIcon={<ClearIcon />} 
          onClick={onClearFilters}
          size="small"
          color="secondary"
        >
          Clear All
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Location Filter */}
        <Grid item xs={12} width="100%">
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Location
          </Typography>
          <TextField
            fullWidth
            label="Search by location"
            value={filters.location}
            onChange={(e) => onFilterChange('location', e.target.value)}
            size="small"
            placeholder="e.g., Canmore, Lake Louise"
          />
        </Grid>

        {/* Temperature Ranges */}
        <Grid item xs={12} width="100%">
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Temperature Ranges
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Snow Temperature (°C)</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Min"
                    type="number"
                    value={filters.snow_temp_min}
                    onChange={(e) => onFilterChange('snow_temp_min', e.target.value)}
                    size="small"
                    inputProps={{ step: "0.1" }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max"
                    type="number"
                    value={filters.snow_temp_max}
                    onChange={(e) => onFilterChange('snow_temp_max', e.target.value)}
                    size="small"
                    inputProps={{ step: "0.1" }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Air Temperature (°C)</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Min"
                    type="number"
                    value={filters.air_temp_min}
                    onChange={(e) => onFilterChange('air_temp_min', e.target.value)}
                    size="small"
                    inputProps={{ step: "0.1" }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max"
                    type="number"
                    value={filters.air_temp_max}
                    onChange={(e) => onFilterChange('air_temp_max', e.target.value)}
                    size="small"
                    inputProps={{ step: "0.1" }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Humidity Ranges */}
        <Grid item xs={12} width="70%">
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Humidity Ranges
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Snow Humidity (Doser)</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Min"
                    type="number"
                    value={filters.snow_humidity_min}
                    onChange={(e) => onFilterChange('snow_humidity_min', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max"
                    type="number"
                    value={filters.snow_humidity_max}
                    onChange={(e) => onFilterChange('snow_humidity_max', e.target.value)}
                    size="small"
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" gutterBottom>Air Humidity (% rH)</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Min"
                    type="number"
                    value={filters.air_humidity_min}
                    onChange={(e) => onFilterChange('air_humidity_min', e.target.value)}
                    size="small"
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max"
                    type="number"
                    value={filters.air_humidity_max}
                    onChange={(e) => onFilterChange('air_humidity_max', e.target.value)}
                    size="small"
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Clouds Range */}
        <Grid item xs={12} sm={6} width="25%">
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Cloud Coverage (0-10)
          </Typography>
          <Typography variant="body2" gutterBottom>Sky parts covered by clouds</Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Min"
                type="number"
                value={filters.clouds_min}
                onChange={(e) => onFilterChange('clouds_min', e.target.value)}
                size="small"
                inputProps={{ min: 0, max: 10 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Max"
                type="number"
                value={filters.clouds_max}
                onChange={(e) => onFilterChange('clouds_max', e.target.value)}
                size="small"
                inputProps={{ min: 0, max: 10 }}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Atmospheric Conditions */}
        <Grid item xs={12} sm={6} width="100%">
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Atmospheric Conditions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} width="30%">
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={filters.visibility}
                  label="Visibility"
                  onChange={(e) => onFilterChange('visibility', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {visibilityOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} width="30%">
              <FormControl fullWidth size="small">
                <InputLabel>Wind</InputLabel>
                <Select
                  value={filters.wind}
                  label="Wind"
                  onChange={(e) => onFilterChange('wind', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {windOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} width="30%">
              <FormControl fullWidth size="small">
                <InputLabel>Precipitation</InputLabel>
                <Select
                  value={filters.precipitation}
                  label="Precipitation"
                  onChange={(e) => onFilterChange('precipitation', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {precipitationOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        {/* Snow Classification */}
        <Grid item xs={12} width="100%">
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Snow Classification
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3} width="20%">
              <FormControl fullWidth size="small">
                <InputLabel>Artificial Snow</InputLabel>
                <Select
                  value={filters.artificial_snow}
                  label="Artificial Snow"
                  onChange={(e) => onFilterChange('artificial_snow', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {artificialSnowOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} width="20%">
              <FormControl fullWidth size="small">
                <InputLabel>Natural Snow</InputLabel>
                <Select
                  value={filters.natural_snow}
                  label="Natural Snow"
                  onChange={(e) => onFilterChange('natural_snow', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {naturalSnowOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} width="20%">
              <FormControl fullWidth size="small">
                <InputLabel>Grain Size</InputLabel>
                <Select
                  value={filters.grain_size}
                  label="Grain Size"
                  onChange={(e) => onFilterChange('grain_size', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {grainSizeOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3} width="20%">
              <FormControl fullWidth size="small">
                <InputLabel>Snow Humidity</InputLabel>
                <Select
                  value={filters.snow_humidity}
                  label="Snow Humidity"
                  onChange={(e) => onFilterChange('snow_humidity', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {snowHumidityOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        {/* Track Conditions */}
        <Grid item xs={12} width="100%">
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
            Track Conditions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} width="30%">
              <FormControl fullWidth size="small">
                <InputLabel>Track Hardness</InputLabel>
                <Select
                  value={filters.track_hardness}
                  label="Track Hardness"
                  onChange={(e) => onFilterChange('track_hardness', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {trackHardnessOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} width="30%">
              <FormControl fullWidth size="small">
                <InputLabel>Track Shininess</InputLabel>
                <Select
                  value={filters.track_shininess}
                  label="Track Shininess"
                  onChange={(e) => onFilterChange('track_shininess', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {trackShininessOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4} width="30%">
              <FormControl fullWidth size="small">
                <InputLabel>Track Dirtiness</InputLabel>
                <Select
                  value={filters.track_dirtiness}
                  label="Track Dirtiness"
                  onChange={(e) => onFilterChange('track_dirtiness', e.target.value)}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {trackDirtinessOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WeatherFilter;
